import { gql } from "config/graphql";
import {
  IInvoice,
  IPaymentMethod,
  MutationResponse,
  PaymentMethodSource,
  SuccessResponse,
} from "config/types";
import { getApolloClient } from "store/apollo";
import { assertDefined } from "utils/error";

interface ICreatePaymentMethodData {
  createPaymentMethod: {
    paymentMethod: IPaymentMethod;
  };
}

export const createPaymentMethodMutation = async (
  externalId: string,
  source: PaymentMethodSource,
  metadata = {},
  setupIntentId?: string
) => {
  const client = getApolloClient();

  const mutation = gql`
    mutation createPaymentMethod($input: CreatePaymentMethodInput!) {
      createPaymentMethod(input: $input) {
        paymentMethod {
          id
          source
          status
          name
          metadata
          externalId
          createdAt
          financialConnectionsAccount {
            id
            status
          }
          updatedAt
        }
      }
    }
  `;

  const { data } = await client.mutate<ICreatePaymentMethodData>({
    variables: {
      input: {
        externalId,
        source,
        metadata,
        ...(setupIntentId && { setupIntentId }),
      },
    },
    mutation,
  });
  return { data: assertDefined(data) };
};

interface ICreateBankAccountPaymentIntentData {
  createBankAccountPaymentIntent: {
    clientSecret: string;
    paymentIntentId: string;
  };
}

export const createBankAccountPaymentIntentMutation = async (invoiceId: string) => {
  const client = getApolloClient();

  const mutation = gql`
    mutation createBankAccountPaymentIntent($input: CreateBankAccountPaymentIntentInput!) {
      createBankAccountPaymentIntent(input: $input) {
        clientSecret
        paymentIntentId
      }
    }
  `;

  const { data } = await client.mutate<ICreateBankAccountPaymentIntentData>({
    variables: { input: { invoiceId } },
    mutation,
  });
  return { data: assertDefined(data) };
};

interface ICreateBankAccountSetupIntentData {
  createBankAccountSetupIntent: {
    clientSecret: string;
  };
}

export const createBankAccountSetupIntentMutation = async () => {
  const client = getApolloClient();

  const mutation = gql`
    mutation createBankAccountSetupIntent($input: CreateBankAccountSetupIntentInput!) {
      createBankAccountSetupIntent(input: $input) {
        clientSecret
      }
    }
  `;

  const { data } = await client.mutate<ICreateBankAccountSetupIntentData>({
    variables: { input: {} },
    mutation,
  });
  return { data: assertDefined(data) };
};

export interface ICreatePaymentInput {
  amount: number;
  invoiceId: string;
}

interface ICreatePaymentData {
  createPayment: SuccessResponse;
}

export const createPaymentMutation: MutationResponse<
  ICreatePaymentInput,
  ICreatePaymentData
> = async (input: ICreatePaymentInput) => {
  const client = getApolloClient();

  const mutation = gql`
    mutation createPayment($input: CreatePaymentInput!) {
      createPayment(input: $input) {
        response {
          success
        }
      }
    }
  `;

  const { data } = await client.mutate<ICreatePaymentData>({
    variables: { input },
    mutation,
  });
  return { data: assertDefined(data) };
};

export interface ICreateMultiplePaymentsInput {
  invoiceIds: IInvoice["id"][];
}

interface ICreateMultiplePaymentsData {
  createMultiplePayments: {
    payments: {
      billingAttemptedAmount: number;
      processed: {
        invoiceId: IInvoice["id"];
        transactionResult: "success" | "failed" | "processing";
        reason?: string;
      }[];
    };
  };
}

export const createMultiplePaymentsMutation: MutationResponse<
  ICreateMultiplePaymentsInput,
  ICreateMultiplePaymentsData
> = async (input: ICreateMultiplePaymentsInput) => {
  const client = getApolloClient();

  const mutation = gql`
    mutation createMultiplePayments($input: CreateMultiplePaymentsInput!) {
      createMultiplePayments(input: $input) {
        payments {
          billingAttemptedAmount
          processed {
            invoiceId
            reason
            transactionResult
          }
        }
      }
    }
  `;

  const { data } = await client.mutate<ICreateMultiplePaymentsData>({
    variables: { input },
    mutation,
  });
  return { data: assertDefined(data) };
};
